import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Save, Trash2 } from 'lucide-react';
import type { AdminCatalog, PresetCategory } from '../../api/imagePresets';
import { presetError } from '../../api/imagePresets';
import { useCreateCategory, useDeleteCategory, useUpdateCategory } from '../../hooks';
import { getErrorDetail } from '../../utils/error';

const errorText = (error: unknown, fallback: string): string => {
  const body = presetError(error);
  if (body?.errors) return body.errors.map((e) => `${e.loc.join('.')}: ${e.msg}`).join(' · ');
  if (body?.used_by) return `${body.detail}`;
  return getErrorDetail(error, fallback);
};

const CategoryRow: React.FC<{ category: PresetCategory; count: number; canEdit: boolean }> = ({
  category,
  count,
  canEdit,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState(category.name);
  const [order, setOrder] = useState(String(category.order));
  const update = useUpdateCategory();
  const remove = useDeleteCategory();
  const dirty = name !== category.name || order !== String(category.order);
  const error = update.error ?? remove.error;

  return (
    <tr className="border-t border-slate-100 align-top">
      <td className="py-2 pr-3 font-mono text-xs text-slate-600 pt-4">{category.id}</td>
      <td className="py-2 pr-3">
        <input
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={!canEdit}
          aria-label={t('imagePresets.categoryName')}
        />
        {error && <p className="mt-1 text-xs text-red-600">{errorText(error, t('imagePresets.saveFailed'))}</p>}
      </td>
      <td className="py-2 pr-3 w-24">
        <input
          className="input tabular-nums"
          type="number"
          value={order}
          onChange={(e) => setOrder(e.target.value)}
          disabled={!canEdit}
          aria-label={t('imagePresets.categoryOrder')}
        />
      </td>
      <td className="py-2 pr-3 pt-4 text-sm text-slate-600 tabular-nums">{count}</td>
      <td className="py-2 text-right whitespace-nowrap">
        {canEdit && (
          <>
            <button
              type="button"
              className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-30"
              disabled={!dirty || update.isPending}
              onClick={() => update.mutate({ id: category.id, changes: { name, order: Number(order) } })}
              aria-label={t('imagePresets.save')}
              title={t('imagePresets.save')}
            >
              <Save size={16} />
            </button>
            <button
              type="button"
              className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30"
              disabled={count > 0 || remove.isPending}
              title={count > 0 ? t('imagePresets.categoryInUse') : t('imagePresets.delete')}
              aria-label={t('imagePresets.delete')}
              onClick={() =>
                window.confirm(t('imagePresets.confirmDeleteCategory', { id: category.id })) &&
                remove.mutate(category.id)
              }
            >
              <Trash2 size={16} />
            </button>
          </>
        )}
      </td>
    </tr>
  );
};

const CategoriesPanel: React.FC<{ catalog: AdminCatalog; canEdit: boolean }> = ({ catalog, canEdit }) => {
  const { t } = useTranslation();
  const create = useCreateCategory();
  const nextOrder = Math.max(0, ...catalog.categories.map((c) => c.order)) + 1;
  const [draft, setDraft] = useState({ id: '', name: '', order: '' });

  const add = async () => {
    try {
      await create.mutateAsync({
        id: draft.id.trim(),
        name: draft.name.trim(),
        order: Number(draft.order || nextOrder),
      });
      setDraft({ id: '', name: '', order: '' });
    } catch {
      // 아래에 보인다.
    }
  };

  return (
    <div className="card p-5 space-y-4">
      <p className="text-sm text-slate-600">{t('imagePresets.categoriesIntro')}</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-500">
              <th className="pb-2 pr-3 font-medium">ID</th>
              <th className="pb-2 pr-3 font-medium">{t('imagePresets.categoryName')}</th>
              <th className="pb-2 pr-3 font-medium">{t('imagePresets.categoryOrder')}</th>
              <th className="pb-2 pr-3 font-medium">{t('imagePresets.presetCount')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {catalog.categories.map((c) => (
              <CategoryRow
                key={`${c.id}-${c.name}-${c.order}`}
                category={c}
                count={catalog.presets.filter((p) => p.category === c.id).length}
                canEdit={canEdit}
              />
            ))}
          </tbody>
        </table>
      </div>
      {canEdit && (
        <div className="border-t border-slate-200 pt-4">
          <p className="text-sm font-medium text-slate-700 mb-2">{t('imagePresets.addCategory')}</p>
          <div className="flex flex-wrap gap-2">
            <input
              className="input font-mono w-40"
              placeholder="id"
              value={draft.id}
              onChange={(e) => setDraft({ ...draft, id: e.target.value })}
              aria-label="id"
            />
            <input
              className="input flex-1 min-w-[10rem]"
              placeholder={t('imagePresets.categoryName')}
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              aria-label={t('imagePresets.categoryName')}
            />
            <input
              className="input w-24"
              type="number"
              placeholder={String(nextOrder)}
              value={draft.order}
              onChange={(e) => setDraft({ ...draft, order: e.target.value })}
              aria-label={t('imagePresets.categoryOrder')}
            />
            <button
              type="button"
              className="btn-primary inline-flex items-center gap-1.5 text-sm"
              onClick={add}
              disabled={!draft.id || !draft.name || create.isPending}
            >
              <Plus size={15} /> {t('imagePresets.add')}
            </button>
          </div>
          {create.error && (
            <p className="mt-2 text-xs text-red-600">{errorText(create.error, t('imagePresets.saveFailed'))}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default CategoriesPanel;
